/**
 * 좌우 분할 패널의 너비 비율을 관리하는 hook.
 *
 * - `containerRef` 는 좌+handle+우 를 감싸는 grid 컨테이너에 부착.
 * - `handleProps` 는 가운데 splitter div 에 spread.
 * - 비율은 좌측 패널 폭의 백분율 (`leftPct`) 로 표현되며 [min, max] 로 clamp.
 * - localStorage 에 자동 persist — 같은 키를 쓰는 모달은 비율을 공유.
 * - 더블클릭 → 기본값 복원.
 *
 * 사용 예:
 *   const split = useResizableSplit({
 *     storageKey: "myModal:splitLeftPct",
 *     defaultPct: 58.33,
 *     min: 30,
 *     max: 75,
 *   });
 *
 *   <div ref={split.containerRef} style={{ gridTemplateColumns:
 *        `${split.leftPct}% 6px 1fr` }}>
 *     <Left />
 *     <div {...split.handleProps} />
 *     <Right />
 *   </div>
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

interface Options {
  storageKey: string;
  defaultPct: number;
  min: number;
  max: number;
}

interface SplitHandleProps {
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (e: ReactPointerEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
}

export interface ResizableSplit {
  // RefObject<HTMLDivElement> 는 React 18 의 useRef<HTMLDivElement>(null) 가 반환하는 표준 형.
  // `.current` 는 자동으로 HTMLDivElement | null 로 추론됨.
  containerRef: React.RefObject<HTMLDivElement>;
  leftPct: number;
  isDragging: boolean;
  handleProps: SplitHandleProps;
}

export function useResizableSplit({ storageKey, defaultPct, min, max }: Options): ResizableSplit {
  const [leftPct, setLeftPct] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw != null) {
        const v = parseFloat(raw);
        if (Number.isFinite(v) && v >= min && v <= max) return v;
      }
    } catch {
      // localStorage 비활성/QuotaExceeded 등은 무시
    }
    return defaultPct;
  });

  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ pointerId: number } | null>(null);

  // persist
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(leftPct));
    } catch {
      // ignore
    }
  }, [storageKey, leftPct]);

  // 드래그 중 body 전체에 cursor/user-select 적용 — 마우스가 다른 요소 위로
  // 가도 일관된 UX 유지.
  useEffect(() => {
    if (!isDragging) return;
    const prevUserSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    return () => {
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
    };
  }, [isDragging]);

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    if (!containerRef.current) return;
    dragRef.current = { pointerId: e.pointerId };
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsDragging(true);
  }, []);

  const handlePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragRef.current || e.pointerId !== dragRef.current.pointerId) return;
      const c = containerRef.current;
      if (!c) return;
      const rect = c.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = (x / rect.width) * 100;
      setLeftPct(Math.max(min, Math.min(max, pct)));
    },
    [min, max],
  );

  const handlePointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || e.pointerId !== dragRef.current.pointerId) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  const handleDoubleClick = useCallback(() => {
    setLeftPct(defaultPct);
  }, [defaultPct]);

  return {
    containerRef,
    leftPct,
    isDragging,
    handleProps: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
      onDoubleClick: handleDoubleClick,
    },
  };
}
